/**
 * FilterBar — the full-width filter strip docked to the top edge of the
 * map. Slimmed to a single thin row (June 2026 client iteration — "the
 * thinner the better").
 *
 * Design contract (catalogue v3, June 2026 filter-bar slim-down):
 *   - One row: [Filter by ▾ dropdown] · [chip row] · [right cluster].
 *   - "Filter by" is a single-line DROPDOWN (not a stacked segmented
 *     control). It lists the kinds in display order — Communities
 *     (default lens) → Theme → Property type — with a prominent caret.
 *     Selecting a kind swaps the active painting / filtering lens via
 *     FilterContext.setActiveKind. Kinds the project hasn't seeded are
 *     shown disabled.
 *   - Active-lens model (D3): only the active kind's chips filter + color
 *     the map. `effectiveKind` comes from FilterContext so the dropdown
 *     label, map coloring, and the filter gate share one source of truth.
 *   - The chip row shows an "All" pill (clears that lens) + the active
 *     kind's chips in FIXED catalogue order (item 1, June 2026: pills must
 *     not jump on select). To keep the strip a sliver we cap the inline
 *     chips at PILL_LIMIT; the remainder collapse behind a "+N more" popover
 *     that flags itself active when a hidden chip is selected.
 *   - Right cluster (far right of the same row, so height never shifts):
 *       · "Contributor" text link → opens the full ContributorsDirectory
 *         drawer (via the onOpenContributors prop).
 *       · ContributorPicker dropdown → quick multi-select map filter.
 *       · "Clear all" → appears only when a content filter is active.
 *   - Empty catalogue (project hasn't been seeded) → render nothing.
 *   - aria-live region announces filter changes for screen readers.
 *
 * Type of Entry, Show My Places, Date Added, and Show Regions no longer
 * live here — they moved to dedicated map/header controls in this
 * iteration (see EntryTypeToggle + MapComponent).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFilter } from "./FilterContext";
import {
  TAXONOMY_KIND,
  KIND_LABELS,
  compareTaxonomiesWithOtherLast,
  isCatchAllTaxonomy,
} from "./utils/taxonomy";
import TaxonomyChip from "./TaxonomyChip";
import ContributorPicker from "./ContributorPicker";
import "./FilterBar.css";

// "Filter by" dropdown order. Communities first because it is the default
// lens (client decision, June 2026), then Theme, then Property type.
const KIND_DROPDOWN_ORDER = [
  TAXONOMY_KIND.COMMUNITY,
  TAXONOMY_KIND.THEME,
  TAXONOMY_KIND.PROPERTY_TYPE,
];

// How many chips render inline before the rest collapse behind "+N more".
// This is a floor on the *inline* count (we expand it when many chips are
// selected), not a hard cap — overflow chips stay reachable via the popover.
const PILL_LIMIT = 6;

// Fixed width of the "+N more" overflow popover.
const MORE_POP_WIDTH = 240;

// Collapse preference (June 2026 client ask): a subtle tab can hide the
// whole filter row for the cleanest map view. Persisted so the choice
// sticks across reloads; defaults to OPEN.
const COLLAPSE_STORAGE_KEY = "whoseland.filterBar.collapsed";
const readCollapsedPref = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";
  } catch (e) {
    return false;
  }
};

export default function FilterBar({ onOpenContributors }) {
  const {
    taxonomies,
    activeThemeIds,
    activePropertyTypeIds,
    activeCommunityIds,
    effectiveKind,
    isAnyActive,
    loading,
    toggleTheme,
    togglePropertyType,
    toggleCommunity,
    setActiveKind,
    clearAll,
  } = useFilter();

  // Split catalogue once per render. Alphabetical sort with catch-all
  // chips ("Other" / "Unspecified") pinned to the end.
  const { themes, propertyTypes, communities } = useMemo(() => {
    const t = [];
    const p = [];
    const c = [];
    for (const tx of taxonomies || []) {
      if (tx.kind === TAXONOMY_KIND.THEME) t.push(tx);
      else if (tx.kind === TAXONOMY_KIND.PROPERTY_TYPE) p.push(tx);
      else if (tx.kind === TAXONOMY_KIND.COMMUNITY) c.push(tx);
    }
    t.sort(compareTaxonomiesWithOtherLast);
    p.sort(compareTaxonomiesWithOtherLast);
    c.sort(compareTaxonomiesWithOtherLast);
    return { themes: t, propertyTypes: p, communities: c };
  }, [taxonomies]);

  const kindData = {
    [TAXONOMY_KIND.THEME]: {
      items: themes,
      activeIds: activeThemeIds,
      toggle: toggleTheme,
    },
    [TAXONOMY_KIND.PROPERTY_TYPE]: {
      items: propertyTypes,
      activeIds: activePropertyTypeIds,
      toggle: togglePropertyType,
    },
    [TAXONOMY_KIND.COMMUNITY]: {
      items: communities,
      activeIds: activeCommunityIds,
      toggle: toggleCommunity,
    },
  };

  // ── "Filter by" dropdown open/close ──────────────────────────────────
  const [kindMenuOpen, setKindMenuOpen] = useState(false);
  const kindRef = useRef(null);
  useEffect(() => {
    if (!kindMenuOpen) return undefined;
    const onClick = (e) => {
      if (kindRef.current && !kindRef.current.contains(e.target)) {
        setKindMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setKindMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [kindMenuOpen]);

  // ── "+N more" overflow popover (fixed-positioned so the strip's own
  //    box never clips it, mirroring ContributorPicker's dropdown) ──────
  const [moreOpen, setMoreOpen] = useState(false);
  const [morePos, setMorePos] = useState(null);
  const moreBtnRef = useRef(null);
  const morePanelRef = useRef(null);

  const recomputeMorePos = useCallback(() => {
    const el = moreBtnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewW = window.innerWidth || document.documentElement.clientWidth;
    let left = rect.left;
    if (left + MORE_POP_WIDTH > viewW - 8) {
      left = Math.max(8, viewW - MORE_POP_WIDTH - 8);
    }
    setMorePos({ top: rect.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!moreOpen) return undefined;
    recomputeMorePos();
    const onScrollOrResize = () => recomputeMorePos();
    const onClick = (e) => {
      const inBtn = moreBtnRef.current && moreBtnRef.current.contains(e.target);
      const inPanel =
        morePanelRef.current && morePanelRef.current.contains(e.target);
      if (!inBtn && !inPanel) setMoreOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen, recomputeMorePos]);

  // Switching the active lens changes the chip set; close any open
  // overflow popover so it can't linger over a now-stale list.
  useEffect(() => {
    setMoreOpen(false);
  }, [effectiveKind]);

  // ── Collapse / expand the whole filter row ───────────────────────────
  const [collapsed, setCollapsed] = useState(readCollapsedPref);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(
          COLLAPSE_STORAGE_KEY,
          next ? "true" : "false",
        );
      } catch (e) {
        /* localStorage unavailable (private mode) — ignore */
      }
      if (next) {
        // Collapsing: close any open menus so they don't reopen on expand.
        setKindMenuOpen(false);
        setMoreOpen(false);
      }
      return next;
    });
  }, []);

  // Hide strip while loading. After load, if nothing is seeded there's
  // no category bar to show.
  if (loading) return null;
  const hasChipCatalogue =
    themes.length > 0 || propertyTypes.length > 0 || communities.length > 0;
  if (!hasChipCatalogue) return null;

  const data = kindData[effectiveKind] || kindData[TAXONOMY_KIND.THEME];
  const rowItems = data?.items || [];
  const activeIds = data?.activeIds || new Set();
  const toggleId = data?.toggle || (() => {});
  if (rowItems.length === 0) return null;

  const labelText = KIND_LABELS[effectiveKind] || "category";
  const allClearAria = `Show all (clear the ${labelText} filter)`;

  // Pill order is FIXED (client revision 2026-06-18, item 1): chips render
  // in stable catalogue order (alphabetical, catch-all last — already sorted
  // by compareTaxonomiesWithOtherLast). We deliberately do NOT float selected
  // chips to the front anymore; the client found pills "jumping left" on
  // select disorienting. We still expand the inline count past PILL_LIMIT
  // when many chips are selected so a wide selection stays visible without
  // reordering.
  const inlineCount = Math.max(PILL_LIMIT, activeIds.size);
  const visibleItems = rowItems.slice(0, inlineCount);
  const overflowItems = rowItems.slice(inlineCount);
  // With fixed order a selected chip can land in the overflow bucket. Flag
  // the "+N more" expander active so a hidden selection is still signposted
  // (preserves the old "never hide an active selection" intent without the
  // reorder).
  const overflowHasActive = overflowItems.some((t) =>
    activeIds.has(Number(t.id)),
  );

  return (
    <div
      className={[
        "filter-bar",
        "filter-bar--slim",
        collapsed ? "filter-bar--row-collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="region"
      aria-label="Filter map"
    >
      {!collapsed && (
      <div className="filter-bar__body">
        <div className="filter-bar__row filter-bar__row--kinds">
          {/* Filter-by dropdown (far left, inline so the row stays thin) */}
          <div className="filter-bar__kind-select" ref={kindRef}>
            <span className="filter-bar__label filter-bar__label--filterby">
              Filter by
            </span>
            <div className="filter-bar__kind-dropdown">
              <button
                type="button"
                className="filter-bar__kind-trigger"
                onClick={() => setKindMenuOpen((s) => !s)}
                aria-haspopup="listbox"
                aria-expanded={kindMenuOpen}
              >
                <span className="filter-bar__kind-trigger-label">
                  {labelText}
                </span>
                <span
                  aria-hidden="true"
                  className="filter-bar__kind-trigger-caret"
                >
                  ▾
                </span>
              </button>
              {kindMenuOpen && (
                <ul
                  className="filter-bar__kind-menu"
                  role="listbox"
                  aria-label="Filter by category"
                >
                  {KIND_DROPDOWN_ORDER.map((k) => {
                    const kd = kindData[k];
                    const empty = !kd || kd.items.length === 0;
                    const isActive = effectiveKind === k;
                    return (
                      <li key={k} role="option" aria-selected={isActive}>
                        <button
                          type="button"
                          className={[
                            "filter-bar__kind-option",
                            isActive ? "filter-bar__kind-option--active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disabled={empty}
                          onClick={() => {
                            setActiveKind(k);
                            setKindMenuOpen(false);
                          }}
                        >
                          {KIND_LABELS[k]}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Active lens's chip row: All + capped chips + "+N more". */}
          <div className="filter-bar__chips">
            <button
              type="button"
              className={[
                "filter-bar__all-pill",
                activeIds.size === 0 ? "filter-bar__all-pill--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                activeIds.forEach((id) => toggleId(id));
              }}
              aria-pressed={activeIds.size === 0}
              aria-label={allClearAria}
            >
              All
            </button>
            {visibleItems.map((t) => (
              <TaxonomyChip
                key={t.id}
                name={t.name}
                color={t.color}
                size="md"
                isActive={activeIds.has(Number(t.id))}
                isCatchAll={isCatchAllTaxonomy(t)}
                onClick={() => toggleId(t.id)}
              />
            ))}
            {overflowItems.length > 0 && (
              <button
                ref={moreBtnRef}
                type="button"
                className={[
                  "filter-bar__more-pill",
                  moreOpen ? "filter-bar__more-pill--open" : "",
                  overflowHasActive ? "filter-bar__more-pill--has-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setMoreOpen((s) => !s)}
                aria-haspopup="true"
                aria-expanded={moreOpen}
                aria-label={
                  overflowHasActive
                    ? `Show ${overflowItems.length} more ${labelText} options (has active filter)`
                    : `Show ${overflowItems.length} more ${labelText} options`
                }
              >
                +{overflowItems.length} more
              </button>
            )}
          </div>

          {/* Right cluster: Contributor link + picker + Clear all. Stays
              on the SAME row so toggling Clear all never changes height. */}
          <div className="filter-bar__right">
            {onOpenContributors && (
              <button
                type="button"
                className="filter-bar__label filter-bar__label--clickable"
                onClick={onOpenContributors}
                aria-label="Open the contributors directory"
              >
                Contributor
              </button>
            )}
            <ContributorPicker />
            {isAnyActive && (
              <button
                type="button"
                className="filter-bar__clear"
                onClick={clearAll}
                aria-label="Clear all active filters"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Collapse tab — a labeled handle centered on the bar's bottom edge
          that hides/shows the whole filter row. The label states the action
          the click performs ("Open Filters" when collapsed, "Close Filters"
          when open) so the affordance is obvious (client ask, July 2026:
          the old wordless tab "virtually disappears"). Default open;
          choice persists. */}
      <button
        type="button"
        className="filter-bar__collapse-tab"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Open filters" : "Close filters"}
        title={collapsed ? "Open filters" : "Close filters"}
      >
        <span aria-hidden="true" className="filter-bar__collapse-caret">
          {collapsed ? "\u25BE" : "\u25B4"}
        </span>
        <span className="filter-bar__collapse-label">
          {collapsed ? "Open Filters" : "Close Filters"}
        </span>
      </button>

      {!collapsed && moreOpen && morePos && overflowItems.length > 0 && (
        <div
          ref={morePanelRef}
          className="filter-bar__more-pop"
          style={{
            position: "fixed",
            top: morePos.top,
            left: morePos.left,
            width: MORE_POP_WIDTH,
          }}
        >
          {overflowItems.map((t) => (
            <TaxonomyChip
              key={t.id}
              name={t.name}
              color={t.color}
              size="md"
              isActive={activeIds.has(Number(t.id))}
              isCatchAll={isCatchAllTaxonomy(t)}
              onClick={() => toggleId(t.id)}
            />
          ))}
        </div>
      )}

      <span aria-live="polite" className="filter-bar__sr">
        {isAnyActive ? "Filters active" : "All filters cleared"}
      </span>
    </div>
  );
}
